import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  TextField,
  Avatar,
  Chip,
  Autocomplete,
  Typography,
  Snackbar,
} from '@mui/material';
import axios from 'axios';
import { getBackendUserUrl } from '../../utils/utils';
import { useCommonEventData } from '../../contexts/EventContext';

const MEMBER_TYPES = {
  HOST: 'Host',
  COLLABORATOR: 'Collaborator'
};

const AddMemberDialog = ({ open, onClose, teamName, onAddMembers, hostCompanyId, addons }) => {
  const { eventId, hostId } = useCommonEventData();
  console.log('AddMemberDialog: open', hostCompanyId);
  const [memberType, setMemberType] = useState('');
  const [selectedAddonCompany, setSelectedAddonCompany] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [addonCompanies, setAddonCompanies] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);

  useEffect(() => {
    const fetchAddonCompanies = async () => {
      try {
        const companyPromises = addons.map(addon => 
          axios.get(`${getBackendUserUrl()}/company/company_details/${addon.company.companyId}`)
        );
        const responses = await Promise.all(companyPromises);
        const companies = responses.map(response => response.data);
        setAddonCompanies(companies);
      } catch (error) {
        console.error('Error fetching addon companies:', error);
      }
    };

    if (addons?.length > 0) {
      fetchAddonCompanies();
    }
  }, [addons]);

  useEffect(() => {
    const fetchUsers = async () => {
      setUsersLoading(true);
      try {
        let response;
        if (memberType === MEMBER_TYPES.HOST && hostCompanyId) {
          console.log('Fetching host company users with hostCompanyId:', hostCompanyId);
          response = await axios.get(`${getBackendUserUrl()}/company/${hostCompanyId}/users`);
          console.log('AddMemberDialog: Host company users response:', response.data);
          if (Array.isArray(response.data) && response.data.length > 0) {
            setAvailableUsers(response.data);
            console.log('AddMemberDialog: Host company users response:', response.data)
          } else {
            setAvailableUsers([]);
          }
        } else if (memberType === MEMBER_TYPES.COLLABORATOR && selectedAddonCompany) {
          console.log('AddMemberDialog: Fetching collaborator company users with selectedAddonCompany:', selectedAddonCompany)
          response = await axios.get(`${getBackendUserUrl()}/company/${selectedAddonCompany.companyId}/users`);
          if (Array.isArray(response.data) && response.data.length > 0) {
            setAvailableUsers(response.data);
            console.log('AddMemberDialog: Collaborator company users response:', response.data)
          } else {
            setAvailableUsers([]);
          }
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        setAvailableUsers([]);
      } finally {
        setUsersLoading(false);
      }
    };

    // Reset users when changing type
    setAvailableUsers([]);
    setSelectedUsers([]);

    if ((memberType === MEMBER_TYPES.HOST && hostCompanyId) || 
        (memberType === MEMBER_TYPES.COLLABORATOR && selectedAddonCompany)) {
      fetchUsers();
    }
  }, [memberType, hostCompanyId, selectedAddonCompany]);

  // Debug log when hostCompanyId changes
  useEffect(() => {
    console.log('hostCompanyId updated:', hostCompanyId);
  }, [hostCompanyId]);

  const handleClose = () => {
    setMemberType('');
    setSelectedAddonCompany(null);
    setSelectedUsers([]);
    onClose();
  };

  const handleSave = () => {
    onAddMembers(selectedUsers, memberType);
    handleClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: '500px',
          maxWidth: '90vw',
        }
      }}
    >
      <DialogTitle>Add Member to {teamName}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
          {/* Member Type Selection */}
          <FormControl fullWidth>
            <InputLabel>Member Type</InputLabel>
            <Select
              value={memberType}
              label="Member Type"
              onChange={(e) => {
                const newType = e.target.value;
                console.log('Selected member type:', newType);
                setMemberType(newType);
                setSelectedAddonCompany(null);
                setSelectedUsers([]);
              }}
            >
              <MenuItem value={MEMBER_TYPES.HOST}>Host</MenuItem>
              <MenuItem value={MEMBER_TYPES.COLLABORATOR}>Collaborator</MenuItem>
            </Select>
          </FormControl>

          {/* Addon Company Selection (only for Collaborators) */}
          {memberType === MEMBER_TYPES.COLLABORATOR && (
            <FormControl fullWidth>
              <Autocomplete
                options={addonCompanies || []}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option) => (
                  <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                    <Avatar src={option.logoUrl} sx={{ width: 24, height: 24, mr: 2 }} />
                    {option.name}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Select Company" />
                )}
                onChange={(_, newValue) => {
                  setSelectedAddonCompany(newValue);
                  setSelectedUsers([]);
                }}
              />
            </FormControl>
          )}

          {/* User Selection */}
          {((memberType === MEMBER_TYPES.HOST) || 
            (memberType === MEMBER_TYPES.COLLABORATOR && selectedAddonCompany)) && (
            <FormControl fullWidth>
              {/* Always show the invite link */}
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography color="text.secondary">
                  Don't see the user you're looking for? Invite them to this company using the link below:
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    color: 'primary.main',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    display: 'inline-block',
                    '&:hover': { color: 'primary.dark' }
                  }}
                  onClick={() => {
                    const companyId = memberType === MEMBER_TYPES.HOST
                      ? hostCompanyId
                      : selectedAddonCompany?.companyId;
                    const inviteLink = `${window.location.origin}/?invite_company_id=${companyId}&event_id=${eventId}&invited_by=${hostId}`;
                    navigator.clipboard.writeText(inviteLink);
                    setInviteCopied(true);
                  }}
                >
                  Copy Invite Link
                </Box>
              </Box>
              {/* Only show the dropdown if there are users */}
              {usersLoading ? (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Loading users...</span>
                </Box>
              ) : availableUsers.length > 0 && (
                <Autocomplete
                  multiple
                  options={Array.isArray(availableUsers) ? availableUsers : []}
                  getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                  renderOption={(props, option) => (
                    <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                      <Box>
                        <div>{`${option.firstName} ${option.lastName}`}</div>
                        <div style={{ fontSize: '0.8em', color: 'text.secondary' }}>{option.email}</div>
                      </Box>
                    </Box>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={`${option.firstName} ${option.lastName}`}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Users"
                      placeholder={availableUsers && availableUsers.length === 0 ? "No users available" : ""}
                    />
                  )}
                  onChange={(_, newValue) => setSelectedUsers(newValue)}
                  noOptionsText={availableUsers && availableUsers.length === 0 ? "No users available" : "Type to search"}
                  disabled={!availableUsers || availableUsers.length === 0}
                />
              )}
            </FormControl>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          disabled={!selectedUsers.length || 
            (memberType === MEMBER_TYPES.COLLABORATOR && !selectedAddonCompany)}
        >
          Add Members
        </Button>
      </DialogActions>
      <Snackbar
        open={inviteCopied}
        autoHideDuration={2000}
        onClose={() => setInviteCopied(false)}
        message="Copied link to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            background: theme => theme.palette.grey[900],
            color: theme => theme.palette.common.white,
            fontWeight: 500,
            borderRadius: 2,
            boxShadow: 3,
            px: 3,
            py: 1.5,
            fontSize: '1rem',
          }
        }}
      />
    </Dialog>
  );
};

export default AddMemberDialog;